import React, { useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../../../context/AuthContext";
import { siteApi } from "../../../lib/api";
import { launchGameById, launchProviderLobby } from "../../../lib/gameLaunch";
import {
  createFavoriteGameItem,
  createFavoriteProviderItem,
  FAVORITES_UPDATED_EVENT,
  getFavoriteGames,
  toggleFavoriteGame,
} from "../../../utils/favoriteGames";
import {
  getGameImage,
  getGameTitle,
  getRows,
  getVendorImages,
  getVendorTitle,
} from "../../../utils/gameCatalog";
import { getRecentPlayAccountKey } from "../../../utils/recentPlayHistory";
import HomeSectionGameCard from "./HomeSectionGameCard";
import { StyledHomeBackendSections } from "./StyledHomeBackendSections";

const POPULAR_LIMIT = 5;

const dispatchAuthRequired = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("crypto-auth-required", {
      detail: { modalOption: "login", reason: "favorite" },
    })
  );
};

const PopularIcon = () => (
  <span className="backend-section-icon popular-play-icon" aria-hidden="true">
    ★
  </span>
);

const interleaveRows = (casinoRows, slotRows) => {
  const maxLength = Math.max(casinoRows.length, slotRows.length);
  const mixed = [];

  for (let index = 0; index < maxLength; index += 1) {
    if (casinoRows[index]) mixed.push(casinoRows[index]);
    if (slotRows[index]) mixed.push(slotRows[index]);
  }

  return mixed;
};

const buildCasinoItem = (vendor) =>
  createFavoriteProviderItem(vendor, {
    label: getVendorTitle(vendor, "카지노게임"),
    gameType: "live-casino",
    meta: "카지노게임",
    kind: "provider",
    image: getVendorImages(vendor)[0] || "",
  });

const buildSlotItem = (game, vendor) =>
  createFavoriteGameItem(game, {
    label: getGameTitle(game, "슬롯게임"),
    gameType: "slots",
    vendorName: vendor?.vendor_name,
    meta: vendor ? getVendorTitle(vendor, "슬롯게임") : "슬롯게임",
    image: getGameImage(game),
  });

const loadTopSlotItemsByVendor = async () => {
  const vendorResponse = await siteApi.getGameVendors({
    game_type: "slots",
    order_by_name: "sequence",
    order_by_type: "ASC",
    limit: POPULAR_LIMIT,
  });

  if (!vendorResponse.success) return [];

  const slotVendors = getRows(vendorResponse)
    .filter((vendor) => vendor?.vendor_name)
    .slice(0, POPULAR_LIMIT);

  const gameResponses = await Promise.allSettled(
    slotVendors.map((vendor) =>
      siteApi.getGames({
        game_type: "slots",
        vendor: vendor.vendor_name,
        order_by_name: "sequence",
        order_by_type: "ASC",
        page: 1,
        limit: 1,
      })
    )
  );

  return gameResponses
    .map((result, index) => {
      if (result.status !== "fulfilled" || !result.value?.success) return null;
      const [game] = getRows(result.value);
      if (!game) return null;
      return buildSlotItem(game, slotVendors[index]);
    })
    .filter(Boolean);
};

const HomePopularGamesSection = () => {
  const railRef = useRef(null);
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [favoriteKeys, setFavoriteKeys] = useState(() => new Set());
  const [launchingKey, setLaunchingKey] = useState("");
  const accountKey = useMemo(() => getRecentPlayAccountKey(user), [user]);

  useEffect(() => {
    let isMounted = true;

    const loadPopularGames = async () => {
      setIsLoading(true);
      try {
        const [casinoResponse, slotItems] = await Promise.all([
          siteApi.getGameVendors({
            game_type: "live-casino",
            order_by_name: "sequence",
            order_by_type: "ASC",
            limit: POPULAR_LIMIT,
          }),
          loadTopSlotItemsByVendor(),
        ]);

        if (!isMounted) return;

        const casinoItems = (casinoResponse.success ? getRows(casinoResponse) : [])
          .slice(0, POPULAR_LIMIT)
          .map(buildCasinoItem);
        setItems(interleaveRows(casinoItems, slotItems));
      } catch {
        if (isMounted) setItems([]);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadPopularGames();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!accountKey) {
      setFavoriteKeys(new Set());
      return undefined;
    }

    const refreshFavorites = () => {
      setFavoriteKeys(new Set(getFavoriteGames(accountKey).map((item) => item.key)));
    };

    refreshFavorites();

    const handleFavoritesUpdated = (event) => {
      if (!event?.detail?.accountKey || event.detail.accountKey === accountKey) {
        refreshFavorites();
      }
    };

    const handleStorage = (event) => {
      if (!event.key || event.key.endsWith(`:${accountKey}`)) {
        refreshFavorites();
      }
    };

    window.addEventListener(FAVORITES_UPDATED_EVENT, handleFavoritesUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(FAVORITES_UPDATED_EVENT, handleFavoritesUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, [accountKey]);

  const scrollByPage = (direction) => {
    if (!railRef.current) return;
    railRef.current.scrollBy({
      left: railRef.current.clientWidth * direction,
      behavior: "smooth",
    });
  };

  const handleFavoriteToggle = (item) => {
    if (!user?.isAuthenticated || !accountKey) {
      dispatchAuthRequired();
      return;
    }

    const result = toggleFavoriteGame(accountKey, item);
    setFavoriteKeys(new Set(result.rows.map((row) => row.key)));
  };

  const handleLaunch = async (item) => {
    if (!item) return;
    setLaunchingKey(item.key);

    try {
      if (item.kind === "provider") {
        await launchProviderLobby(
          {
            vendor_name: item.vendorName,
            vendor_name_kr: item.title,
            lobby_id: item.lobbyId,
            vendor_list: {
              lobby_id: item.lobbyId,
              thumbnails: item.image,
            },
            image: item.image,
            game_type: item.gameType,
          },
          {
            label: item.title,
            gameType: item.gameType,
            meta: item.meta,
          }
        );
        return;
      }

      await launchGameById(item.gameId, {
        label: item.title,
        recentPlay: item,
      });
    } finally {
      setLaunchingKey("");
    }
  };

  if (!isLoading && items.length === 0) return null;

  return (
    <StyledHomeBackendSections className="home-popular-wrapper">
      <section className="backend-section popular-section">
        <div className="backend-section-header">
          <div className="backend-section-title">
            <PopularIcon />
            <div>
              <div className="backend-section-name">인기게임</div>
            </div>
          </div>

          <div className="backend-section-actions">
            <button type="button" onClick={() => scrollByPage(-1)} aria-label="인기게임 이전">
              ‹
            </button>
            <button type="button" onClick={() => scrollByPage(1)} aria-label="인기게임 다음">
              ›
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="backend-card-rail is-skeleton-rail" aria-hidden="true">
            {Array.from({ length: 8 }).map((_, index) => (
              <article className="backend-card skeleton-card" key={`popular-skeleton-${index}`}>
                <span className="skeleton-title" />
              </article>
            ))}
          </div>
        ) : (
          <div className="backend-card-rail recent-play-rail" ref={railRef}>
            {items.map((item, index) => (
              <HomeSectionGameCard
                key={item.key}
                item={item}
                index={index}
                isLaunching={launchingKey === item.key}
                isFavorite={favoriteKeys.has(item.key)}
                onFavoriteToggle={handleFavoriteToggle}
                onLaunch={handleLaunch}
              />
            ))}
          </div>
        )}
      </section>
    </StyledHomeBackendSections>
  );
};

export default HomePopularGamesSection;
